import renderMinicart from '../../lib/renderMinicart';
import getCart from '../../lib/getCart';
import updateCartCount from '../../lib/updateCartCount';
import { SELECTORS, HIDDEN_CLASS, SWATCHES } from '../../utils/constants';

const POINTER_EVENTS_NONE = 'pointer-events-none';
const LOADING_OPACITY = 'opacity-60';
const CLOSE_DELAY_MS = 1500;


const selectedVariants = new Set();
let existingSwatchCount = 0;
let closeDrawerTimeout = null;

const fetchExistingSwatchCount = async () => {
  try {
    const cart = await getCart();
    if (!cart) return 0;
    return cart.items
      .filter((item) => item.properties && item.properties[SWATCHES.PROPERTY_KEY] === 'true')
      .reduce((sum, item) => sum + item.quantity, 0);
  } catch (error) {
    console.warn('Could not fetch cart for swatch count:', error);
    return 0;
  }
};

const updateUI = () => {
  const addBtn = document.querySelector('[data-swatch-add-to-cart]');
  const btnLabel = document.querySelector('[data-swatch-btn-label]');
  const limitMsg = document.querySelector('[data-swatch-limit-message]');
  const cards = document.querySelectorAll('[data-swatch-card]');

  const count = selectedVariants.size;
  const totalSelected = count + existingSwatchCount;
  const remaining = SWATCHES.MAX_QUANTITY - totalSelected;
  const atCartLimit = existingSwatchCount >= SWATCHES.MAX_QUANTITY;

  cards.forEach((card) => {
    card.disabled = atCartLimit || (!card.dataset.selected && remaining <= 0);
  });

  if (addBtn) {
    addBtn.disabled = count === 0 || atCartLimit;

    if (btnLabel) {
      if (count === 0) {
        btnLabel.textContent = addBtn.dataset.defaultLabel ?? '';
      } else if (count === 1) {
        btnLabel.textContent = addBtn.dataset.singleLabel ?? '';
      } else {
        btnLabel.textContent = (addBtn.dataset.pluralLabel ?? '').replace('{count}', String(count));
      }
    }
  }

  if (limitMsg) {
    const { strings } = window.theme;
    if (atCartLimit) {
      limitMsg.textContent = strings.swatchAtLimit.replace('{{ max }}', SWATCHES.MAX_QUANTITY);
    } else if (existingSwatchCount > 0 && count === 0) {
      const template = existingSwatchCount === 1 ? strings.swatchInBasketOne : strings.swatchInBasketOther;
      limitMsg.textContent = template.replace('{{ count }}', existingSwatchCount).replace('{{ remaining }}', remaining);
    } else if (remaining <= 0) {
      limitMsg.textContent = strings.swatchMaxReached.replace('{{ max }}', SWATCHES.MAX_QUANTITY);
    } else {
      limitMsg.textContent = '';
    }
  }
};

const initFabricSwatches = () => {
  document.addEventListener('click', async (event) => {
    if (event.target.closest('[data-drawer-open="fabric-swatches"]')) {
      clearTimeout(closeDrawerTimeout);
      closeDrawerTimeout = null;
      selectedVariants.clear();
      existingSwatchCount = await fetchExistingSwatchCount();
      updateUI();
      return;
    }

    const card = event.target.closest('[data-swatch-card]');
    if (card) {
      const { variantId } = card.dataset;
      if (!variantId) return;

      if (selectedVariants.has(variantId)) {
        selectedVariants.delete(variantId);
        delete card.dataset.selected;
        card.setAttribute('aria-pressed', 'false');
      } else {
        if (selectedVariants.size + existingSwatchCount >= SWATCHES.MAX_QUANTITY) return;
        selectedVariants.add(variantId);
        card.dataset.selected = 'true';
        card.setAttribute('aria-pressed', 'true');
      }

      updateUI();
      return;
    }

    const addBtn = event.target.closest('[data-swatch-add-to-cart]');
    if (!addBtn || selectedVariants.size === 0) return;

    const spinner = addBtn.querySelector(SELECTORS.LOADING_SPINNER);
    const btnLabel = addBtn.querySelector('[data-swatch-btn-label]');

    addBtn.disabled = true;
    addBtn.classList.add(POINTER_EVENTS_NONE, LOADING_OPACITY);
    spinner?.classList.remove(HIDDEN_CLASS);

    try {
      const latestCount = await fetchExistingSwatchCount();
      if (latestCount + selectedVariants.size > SWATCHES.MAX_QUANTITY) {
        existingSwatchCount = latestCount;
        updateUI();
        return;
      }

      const count = selectedVariants.size;
      const items = Array.from(selectedVariants).map((id) => ({
        id: parseInt(id, 10),
        quantity: 1,
        properties: { [SWATCHES.PROPERTY_KEY]: 'true' },
      }));

      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Theme-ATC': '1' },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || errorData.message || `Cart add failed: ${response.status}`);
      }

      const [cart] = await Promise.all([getCart(), renderMinicart()]);
      updateCartCount(cart.item_count);

      spinner?.classList.add(HIDDEN_CLASS);
      addBtn.classList.remove(POINTER_EVENTS_NONE, LOADING_OPACITY);

      if (btnLabel) {
        const { strings } = window.theme;
        btnLabel.textContent = count === 1
          ? strings.swatchAddedOne
          : strings.swatchAddedOther.replace('{{ count }}', count);
      }

      existingSwatchCount = latestCount + count;
      selectedVariants.clear();

      closeDrawerTimeout = setTimeout(() => {
        document.querySelector('[data-site-drawer-close]')?.click();
        closeDrawerTimeout = null;
      }, CLOSE_DELAY_MS);
    } catch (error) {
      console.error('Error adding swatches to cart:', error);
      addBtn.disabled = false;
      addBtn.classList.remove(POINTER_EVENTS_NONE, LOADING_OPACITY);
      spinner?.classList.add(HIDDEN_CLASS);
    }
  });
};

export default initFabricSwatches;
